import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardHero, ArticleCardStandard, ArticleCardCompact, ArticleCardSidebar } from '../components/cards/ArticleCard';
import AuthModal from '../components/modals/AuthModal';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Sparkles, Clock, ChevronRight, Mail, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function HomePage() {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [widgetLightboxOpen, setWidgetLightboxOpen] = useState(false);
  const widgetInitialized = useRef(false);

  useEffect(() => {
    fetchAllContent();
  }, []);

  // Open widget lightbox after 10 seconds
  useEffect(() => {
    const hasSeenWidget = sessionStorage.getItem('widgetShown');
    if (hasSeenWidget) return;

    const timer = setTimeout(() => {
      setWidgetLightboxOpen(true);
      sessionStorage.setItem('widgetShown', 'true');
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  // Initialize IntentNexus widget when lightbox opens
  useEffect(() => {
    if (widgetLightboxOpen && !widgetInitialized.current) {
      const script = document.createElement('script');
      script.src = 'https://market-pulse-1087.preview.emergentagent.com/widget.js';
      script.async = true;
      script.onload = () => {
        if (window.IntentNexus) {
          window.IntentNexus.init({
            flowId: 'f9b1774b-5cd8-4871-8e35-f1cf9b70312d',
            apiUrl: 'https://market-pulse-1087.preview.emergentagent.com/api',
            container: '#intentnexus-widget'
          });
          widgetInitialized.current = true;
        }
      };
      document.head.appendChild(script);
    }
  }, [widgetLightboxOpen]);

  const fetchAllContent = async () => {
    try {
      const [featuredRes, latestRes, trendingRes] = await Promise.all([
        axios.get(`${API}/articles/featured`, { params: { limit: 5 } }),
        axios.get(`${API}/articles`, { params: { limit: 12 } }),
        axios.get(`${API}/articles/trending`, { params: { limit: 5 } }),
      ]);

      setFeaturedArticles(featuredRes.data);
      setLatestArticles(latestRes.data);
      setTrendingArticles(trendingRes.data);

      const categoryData = {};
      for (const cat of categories.slice(0, 4)) {
        try {
          const res = await axios.get(`${API}/articles`, { params: { category: cat.slug, limit: 4 } });
          categoryData[cat.slug] = res.data;
        } catch (e) {
          categoryData[cat.slug] = [];
        }
      }
      setCategoryArticles(categoryData);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <StockTicker />
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section py-4 sm:py-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <Skeleton className="w-full h-[280px] sm:h-[400px] lg:h-[500px] rounded-sm" />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[240px] rounded-sm" />
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[240px] rounded-sm" />
              </div>
            </div>
          ) : featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <ArticleCardHero article={featuredArticles[0] || latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                {(featuredArticles.slice(1, 3).length > 0 ? featuredArticles.slice(1, 3) : latestArticles.slice(1, 3)).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : latestArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <ArticleCardHero article={latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                {latestArticles.slice(1, 3).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-400">No articles available yet. Content is being fetched...</p>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6 sm:py-12" data-testid="main-content">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            {/* Articles Grid */}
            <div className="lg:col-span-8">
              {/* Latest News */}
              <div className="mb-8 sm:mb-12">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="flex items-center text-lg sm:text-2xl font-bold text-gray-900 border-b-0 pb-0 mb-0" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <Clock className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-[#1D4ED8]" strokeWidth={1.5} />
                    <span className="border-b-2 border-[#1D4ED8] pb-1">Latest News</span>
                  </h2>
                </div>
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[250px] sm:h-[300px] rounded-sm" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {latestArticles.slice(0, 6).map((article) => (
                      <ArticleCardStandard key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {/* Category Sections */}
              {categories.slice(0, 3).map((cat) => (
                <div key={cat.slug} className="mb-8 sm:mb-12">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-xl font-bold text-gray-900 border-b-2 border-[#1D4ED8] pb-1 mb-0" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {cat.name}
                    </h2>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center text-xs sm:text-sm text-[#1D4ED8] hover:text-[#1E3A8A] transition-colors font-medium"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                    </Link>
                  </div>
                  {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {[...Array(2)].map((_, i) => (
                        <Skeleton key={i} className="w-full h-[280px] rounded-sm" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {(categoryArticles[cat.slug] || latestArticles.filter(a => a.category === cat.slug)).slice(0, 4).map((article) => (
                        <ArticleCardStandard key={article.id} article={article} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6 sm:space-y-8">
              {/* Newsletter Widget */}
              <div className="p-4 sm:p-6 rounded-lg bg-gradient-to-br from-[#1D4ED8] to-[#1E3A8A] shadow-lg">
                <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Stay Updated
                </h3>
                <p className="text-blue-100 text-xs sm:text-sm mb-3 sm:mb-4">
                  Get the latest financial news and insights delivered to your inbox.
                </p>
                <Button
                  onClick={() => setAuthModalOpen(true)}
                  className="w-full bg-white text-[#1D4ED8] hover:bg-blue-50 font-semibold uppercase tracking-wide text-xs sm:text-sm rounded-sm"
                  data-testid="subscribe-btn"
                >
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Subscribe
                </Button>
              </div>

              {/* Trending */}
              <div className="sidebar-widget p-4 sm:p-6 rounded-lg border-l-4 border-l-[#1D4ED8]">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-[#1D4ED8]" strokeWidth={1.5} />
                  Trending
                </h3>
                <div className="space-y-0">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[60px] mb-4 rounded-sm" />
                    ))
                  ) : trendingArticles.length > 0 ? (
                    trendingArticles.map((article, index) => (
                      <ArticleCardCompact key={article.id} article={article} index={index} />
                    ))
                  ) : latestArticles.slice(0, 5).map((article, index) => (
                    <ArticleCardCompact key={article.id} article={article} index={index} />
                  ))}
                </div>
              </div>

              {/* Editor's Picks */}
              <div className="sidebar-widget p-4 sm:p-6 rounded-lg border-l-4 border-l-[#1D4ED8]">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-[#1D4ED8]" strokeWidth={1.5} />
                  Editor's Picks
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[70px] sm:h-[80px] rounded-sm" />
                    ))
                  ) : (featuredArticles.length > 0 ? featuredArticles : latestArticles).slice(0, 5).map((article) => (
                    <ArticleCardSidebar key={article.id} article={article} />
                  ))}
                </div>
              </div>

              {/* Advertise CTA */}
              <div className="p-4 sm:p-6 rounded-lg bg-[#0F172A] border border-slate-700">
                <h3 className="text-base sm:text-lg font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Advertise With Us
                </h3>
                <p className="text-slate-400 text-xs sm:text-sm mb-3 sm:mb-4">
                  Reach thousands of finance-focused readers with your message.
                </p>
                <Link
                  to="/advertise"
                  className="inline-block bg-[#1D4ED8] text-white px-4 py-2 text-xs sm:text-sm font-semibold uppercase tracking-wide rounded-sm hover:bg-[#1E3A8A] transition-colors"
                  data-testid="advertise-cta"
                >
                  Learn More
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
      
      {/* IntentNexus Widget Lightbox */}
      {widgetLightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setWidgetLightboxOpen(false);
          }}
          data-testid="widget-lightbox"
        >
          <div className="relative bg-white border border-gray-200 rounded-md shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setWidgetLightboxOpen(false)}
              className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-900 transition-colors z-10"
              data-testid="widget-lightbox-close"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="p-6">
              <div id="intentnexus-widget"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
